import type { Session } from '@supabase/supabase-js'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { clearPersistedSession, supabase } from '@/lib/supabase/client'

export type AuthContextValue = Readonly<{
  session: Session | null
  loading: boolean
  initializationError: string | null
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
}>

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({
  children,
  client = supabase,
}: {
  children: ReactNode
  client?: typeof supabase
}) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [initializationError, setInitializationError] = useState<string | null>(
    null,
  )
  useEffect(() => {
    let active = true
    void client.auth.getSession().then(({ data, error }) => {
      if (!active) return
      if (error)
        setInitializationError('The saved session could not be restored.')
      setSession(data.session)
      setLoading(false)
    })

    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (active) {
        setSession(nextSession)
        setLoading(false)
      }
    })

    return () => {
      active = false
      data.subscription.unsubscribe()
    }
  }, [client])

  const requireOnline = useCallback(() => {
    if (!navigator.onLine) throw new Error('Network connection required')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      initializationError,
      signIn: async (email, password) => {
        requireOnline()
        const { data, error } = await client.auth.signInWithPassword({
          email,
          password,
        })
        if (error) throw error
        if (!data.session) throw new Error('Authentication session was not returned.')
        setSession(data.session)
        setLoading(false)
      },
      signUp: async (email, password) => {
        requireOnline()
        const { error } = await client.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        })
        if (error) throw error
      },
      signOut: async () => {
        const { error } = await client.auth.signOut()
        if (error) {
          // The server could not be told. End the session on this device
          // anyway, so a shared device cannot be reloaded back into the
          // previous user's session.
          clearPersistedSession()
        }
        setSession(null)
        setLoading(false)
      },
      requestPasswordReset: async (email) => {
        requireOnline()
        const { error } = await client.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        })
        if (error) throw error
      },
      updatePassword: async (password) => {
        requireOnline()
        const { error } = await client.auth.updateUser({ password })
        if (error) throw error
      },
    }),
    [client, initializationError, loading, requireOnline, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider.')
  return value
}
